import { Navigate } from 'react-router-dom';

import Box from '@mui/material/Box';

import mainBackgroundImage from '@/assets/welcomeBackgroundImage.png';
import type { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';

import { Button } from '@/components/atoms/Button';
import { Popup } from '@/components/atoms/Popup';

import { LoginCard } from './components/LoginCard';
import { LoginFooter } from './components/LoginFooter';
import { LoginMarketingCopy } from './components/LoginMarketingCopy';
import { useLoginForm } from './hooks/useLoginForm';

/* Highlight box positions — shared between the SVG mask cutouts and the
   border/dot overlays so they stay perfectly in sync. */
const HIGHLIGHTS = [
  { left: '11.2%', top: '54.3%', width: '16%', height: '28.7%', delay: 0.4 },
  { left: '79.5%', top: '61.9%', width: '13.8%', height: '24.7%', delay: 1.0 },
] as const;

const DOT_SIZE = 8;

const highlightKeyframes = `
  @keyframes highlight-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes draw-border {
    from {
      clip-path: inset(0 100% 100% 0);
    }
    to {
      clip-path: inset(0 0 0 0);
    }
  }
  @keyframes veil-out {
    from { opacity: 1; }
    to   { opacity: 0; }
  }
  @keyframes draw-grid {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

/**
 * SVG dark overlay with mask cutouts.
 * White areas in the mask = dark overlay shown (dimmed).
 * Black rectangles in the mask = overlay hidden (bright original image).
 */
function SpotlightOverlay() {
  return (
    <Box
      component="svg"
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
        display: { xs: 'none', md: 'block' },
      }}
    >
      <defs>
        <mask id="spotlight-mask">
          <rect width="100%" height="100%" fill="white" />
          {HIGHLIGHTS.map((h, i) => (
            <rect key={i} x={h.left} y={h.top} width={h.width} height={h.height} fill="black" />
          ))}
        </mask>
      </defs>
      <rect width="100%" height="100%" fill="rgba(0,0,0,0.50)" mask="url(#spotlight-mask)" />
    </Box>
  );
}

function FurnitureHighlightBox({
  left,
  top,
  width,
  height,
  delay,
}: {
  left: string;
  top: string;
  width: string;
  height: string;
  delay: number;
}) {
  const dotSx = {
    position: 'absolute' as const,
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: '50%',
    bgcolor: 'white',
    zIndex: 2,
  };

  const gridLineSx = {
    position: 'absolute' as const,
    bgcolor: 'rgba(255,255,255,0.35)',
  };

  const gridDelay = delay + 0.8;
  const dotDelay = delay + 0.5;

  return (
    <Box
      sx={{
        position: 'absolute',
        left,
        top,
        width,
        height,
      }}
    >
      {/* Dark veil — matches overlay colour exactly, fades out as the border draws */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.50)',
          animation: `veil-out 1.2s ease-out ${delay}s forwards`,
          zIndex: 0,
        }}
      />

      {/* White border — draws from top-left corner */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          border: '1.5px solid rgba(255,255,255,0.75)',
          borderRadius: '2px',
          zIndex: 1,
          clipPath: 'inset(0 100% 100% 0)',
          animation: `draw-border 1.2s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s forwards`,
        }}
      />

      {/* Grid lines — fade in after border draws */}
      <Box
        sx={{
          ...gridLineSx,
          left: '33.33%',
          top: 0,
          bottom: 0,
          width: '1px',
          opacity: 0,
          animation: `draw-grid 0.5s ease-out ${gridDelay}s forwards`,
        }}
      />
      <Box
        sx={{
          ...gridLineSx,
          left: '66.66%',
          top: 0,
          bottom: 0,
          width: '1px',
          opacity: 0,
          animation: `draw-grid 0.5s ease-out ${gridDelay + 0.1}s forwards`,
        }}
      />
      <Box
        sx={{
          ...gridLineSx,
          top: '33.33%',
          left: 0,
          right: 0,
          height: '1px',
          opacity: 0,
          animation: `draw-grid 0.5s ease-out ${gridDelay + 0.2}s forwards`,
        }}
      />
      <Box
        sx={{
          ...gridLineSx,
          top: '66.66%',
          left: 0,
          right: 0,
          height: '1px',
          opacity: 0,
          animation: `draw-grid 0.5s ease-out ${gridDelay + 0.3}s forwards`,
        }}
      />

      {/* Corner dots — pop in as border reaches each corner */}
      <Box
        sx={{
          ...dotSx,
          top: -DOT_SIZE / 2,
          left: -DOT_SIZE / 2,
          opacity: 0,
          animation: `highlight-fade-in 0.3s ease-out ${delay}s forwards`,
        }}
      />
      <Box
        sx={{
          ...dotSx,
          top: -DOT_SIZE / 2,
          right: -DOT_SIZE / 2,
          opacity: 0,
          animation: `highlight-fade-in 0.3s ease-out ${dotDelay}s forwards`,
        }}
      />
      <Box
        sx={{
          ...dotSx,
          bottom: -DOT_SIZE / 2,
          left: -DOT_SIZE / 2,
          opacity: 0,
          animation: `highlight-fade-in 0.3s ease-out ${dotDelay + 0.3}s forwards`,
        }}
      />
      <Box
        sx={{
          ...dotSx,
          bottom: -DOT_SIZE / 2,
          right: -DOT_SIZE / 2,
          opacity: 0,
          animation: `highlight-fade-in 0.3s ease-out ${dotDelay + 0.6}s forwards`,
        }}
      />

      {/* Edge midpoint dots — appear after corners */}
      <Box
        sx={{
          ...dotSx,
          top: -DOT_SIZE / 2,
          left: '50%',
          transform: 'translateX(-50%)',
          opacity: 0,
          animation: `highlight-fade-in 0.3s ease-out ${dotDelay + 0.15}s forwards`,
        }}
      />
      <Box
        sx={{
          ...dotSx,
          bottom: -DOT_SIZE / 2,
          left: '50%',
          transform: 'translateX(-50%)',
          opacity: 0,
          animation: `highlight-fade-in 0.3s ease-out ${dotDelay + 0.45}s forwards`,
        }}
      />
      <Box
        sx={{
          ...dotSx,
          left: -DOT_SIZE / 2,
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: 0,
          animation: `highlight-fade-in 0.3s ease-out ${dotDelay + 0.15}s forwards`,
        }}
      />
      <Box
        sx={{
          ...dotSx,
          right: -DOT_SIZE / 2,
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: 0,
          animation: `highlight-fade-in 0.3s ease-out ${dotDelay + 0.45}s forwards`,
        }}
      />
    </Box>
  );
}

function FurnitureHighlightBoxes() {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        pointerEvents: 'none',
        display: { xs: 'none', md: 'block' },
      }}
    >
      <style>{highlightKeyframes}</style>
      {HIGHLIGHTS.map((h, i) => (
        <FurnitureHighlightBox key={i} {...h} />
      ))}
    </Box>
  );
}

function selectIsAuthed(state: RootState) {
  return Boolean(state.auth.token ?? state.auth.user?.token);
}

function LoginPageContent() {
  const {
    email,
    setEmail,
    otp,
    setOtp,
    otpSent,
    otpTimeout,
    otpResendCount,
    isSendingOtp,
    isVerifyingOtp,
    isGoogleSigningIn,
    sendOtp,
    resendOtp,
    verifyOtp,
    resetEmailState,
    signInWithGoogle,
    contactOpen,
    openContact,
    closeContact,
  } = useLoginForm();

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${mainBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <SpotlightOverlay />

      <LoginMarketingCopy />

      <FurnitureHighlightBoxes />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 8, sm: 6 },
          background: 'transparent',
        }}
      >
        <LoginCard
          email={email}
          onEmailChange={setEmail}
          otp={otp}
          onOtpChange={setOtp}
          otpSent={otpSent}
          otpTimeout={otpTimeout}
          otpResendCount={otpResendCount}
          isSendingOtp={isSendingOtp}
          isVerifyingOtp={isVerifyingOtp}
          isGoogleSigningIn={isGoogleSigningIn}
          onSendOtp={sendOtp}
          onResendOtp={resendOtp}
          onVerifyOtp={verifyOtp}
          onChangeEmail={resetEmailState}
          onGoogleSignIn={signInWithGoogle}
          onContactAdmin={openContact}
        />
      </Box>

      <LoginFooter />

      <Popup
        open={contactOpen}
        onClose={closeContact}
        title="Contact your administrator"
        actions={
          <Button onClick={closeContact} sx={{ minWidth: 96 }}>
            Close
          </Button>
        }
      >
        If you need access to FF&E, reach out to your organization&apos;s administrator so they can
        invite you or restore your account.
      </Popup>
    </Box>
  );
}

export default function LoginPage() {
  const authed = useAppSelector(selectIsAuthed);

  if (authed) {
    return <Navigate to="/home" replace />;
  }

  return <LoginPageContent />;
}
